/**
 * AnalysisFeedRow — Single row in the "Recent Analyses" list.
 *
 * The row itself carries a full accessibility sentence via accessibilityLabel;
 * ScoreChip is hidden from the tree (accessibilityElementsHidden).
 *
 * minHeight: 60 exceeds the 44px WCAG touch target minimum.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { ClientAvatar } from './ClientAvatar';
import { ScoreChip } from './ScoreChip';

interface AnalysisFeedRowProps {
  id: string;
  clientName: string;
  clientInitials: string;
  avatarIndex: number;
  patternLabel: string;
  formattedDate: string;
  dramaScore: number;
  onPress: (id: string) => void;
  t: TFunction<'practitioner'>;
}

export const AnalysisFeedRow: React.FC<AnalysisFeedRowProps> = React.memo(function AnalysisFeedRow({
  id,
  clientName,
  clientInitials,
  avatarIndex,
  patternLabel,
  formattedDate,
  dramaScore,
  onPress,
  t,
}) {
  return (
    <Pressable
      style={({ pressed }) => [
        styles.row,
        pressed && styles.rowPressed,
      ]}
      onPress={() => onPress(id)}
      accessibilityRole="button"
      accessibilityLabel={t('accessibility.analysis_row', {
        client: clientName,
        pattern: patternLabel,
        date: formattedDate,
        score: dramaScore,
      })}
    >
      <ClientAvatar initials={clientInitials} gradientIndex={avatarIndex} size={36} />

      <View style={styles.textGroup}>
        <View style={styles.nameRow}>
          <Text style={styles.clientName} numberOfLines={1}>
            {clientName}
          </Text>
          <ScoreChip value={dramaScore} />
        </View>
        <Text style={styles.meta} numberOfLines={1}>
          {patternLabel} · {formattedDate}
        </Text>
      </View>

      <Ionicons name="chevron-forward" size={16} color="#D4D4D8" />
    </Pressable>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 13,
    paddingHorizontal: 16,
    minHeight: 60,
  },
  rowPressed: {
    opacity: 0.75,
  },
  textGroup: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  clientName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#18181B',
    flexShrink: 1,
  },
  meta: {
    fontSize: 12,
    color: '#A1A1AA',
    marginTop: 2,
  },
});
